/*
 * Generated by tutor-mfe for all.
 *
 * Frontend-base counterpart of the legacy env.config.jsx.
 */

import React, { useEffect, useState } from 'react';
import Cookies from 'universal-cookie';

import { getConfig } from '@edx/frontend-platform';
import { getAuthenticatedHttpClient, getAuthenticatedUser } from '@edx/frontend-platform/auth';
import { Icon } from '@openedx/paragon';
import { Nightlight, WbSunny } from '@openedx/paragon/icons';
import { useIntl } from '@edx/frontend-platform/i18n';
import { getAuthenticatedHttpClient as sqaGetHttpClient } from '@edx/frontend-platform/auth';

import { DIRECT_PLUGIN, IFRAME_PLUGIN, PLUGIN_OPERATIONS } from '@openedx/frontend-plugin-framework';

function addPlugins(config, slot_name, plugins) {
  if (slot_name in config.pluginSlots === false) {
    config.pluginSlots[slot_name] = {
      keepDefault: true,
      plugins: []
    };
  }
  config.pluginSlots[slot_name].plugins.push(...plugins);
}
let themeVariant = 'selected-paragon-theme-variant';

const AddDarkTheme = () => {
  const isThemeToggleEnabled = getConfig().INDIGO_ENABLE_DARK_TOGGLE;

  const addDarkThemeToIframes = () => {
    const iframes = document.getElementsByTagName('iframe');
    const iframesLength = iframes.length;
    if (iframesLength > 0) {
      Array.from({ length: iframesLength }).forEach((_, index) => {
        const style = document.createElement('style');
        style.textContent = `
          body {
            background-color: #0A1226;
            color: #C2CDE8;
          }
          a { color: #6FA8FF; }
          a:hover { color: #E9EEFA; }
        `;
        if (iframes[index].contentDocument) {
          iframes[index].contentDocument.head.appendChild(style);
        }
      });
    }
  };

  useEffect(() => {
    const theme = window.localStorage.getItem(themeVariant);

    // - When page loads, Footer loads before MFE content. Since there is no iframe on page,
    // it does not append any class. MutationObserver observes changes in DOM and hence appends dark
    // attributes when iframe is added. After 15 sec, this observer is destroyed to conserve resources. 
    // - It has been added outside dark-theme condition so that it can be removed on Component Unmount.
    // - Observer can be passed to `addDarkThemeToIframes` function and disconnected after observing Iframe.
    // This approach has a limitation: the observer first detects the iframe and then detects the docSrc. 
    // We need to wait for docSrc to fully load before appending the style tag.
    const observer = new MutationObserver(() => {
      addDarkThemeToIframes();
    });

    if (isThemeToggleEnabled && theme === 'dark') {
      document.documentElement.setAttribute('data-paragon-theme-variant', 'dark');

      observer.observe(document.body, { childList: true, subtree: true });
      setTimeout(() => observer?.disconnect(), 15000); // clear after 15 sec to avoid resource usage
    }

    return () => observer?.disconnect();
  }, []);

  return (<div />);
};

function setConfig() {
  const config = {
    pluginSlots: {},
  };
const IndigoFooter = () => {
  const intl = useIntl();
  const config = getConfig();

  const indigoFooterNavLinks = config.INDIGO_FOOTER_NAV_LINKS || [];
  // Per-site brand (tutor-indigo INDIGO_BRAND_* settings). The fallbacks are
  // Stem Quest Academy's, for an LMS whose settings predate these keys.
  const brandName = config.INDIGO_BRAND_NAME || "Stem Quest Academy";
  const brandUrl = config.INDIGO_BRAND_URL === undefined
    ? "https://stemquestacademy.com/"
    : (config.INDIGO_BRAND_URL || config.LMS_BASE_URL);
  const brandIsExternal = brandUrl.indexOf(config.LMS_BASE_URL) !== 0;
  // The footer is a night band in both themes: a site logo is its light wordmark.
  const logoUrl = config.INDIGO_LOGO_WHITE_URL || `${config.LMS_BASE_URL}/theming/asset/images/logo.png`;

  const messages = {
    "footer.logo.altText": {
      id: "footer.logo.altText",
      defaultMessage: brandName,
      description: "alt text for the footer logo.",
    },
    "footer.copyright.text": {
      id: "footer.copyright.text",
      defaultMessage: `© ${new Date().getFullYear()} ${brandName}. All Rights Reserved.`,
      description: "copyright text for the footer",
    },
  };

  return (
    <div className="wrapper wrapper-footer">
      <footer id="footer" className="tutor-container">
        <div className="footer-top">
          <div className="powered-area">
            <ul className="logo-list">
              <li>
                <a
                  href={brandUrl}
                  rel="noreferrer"
                  target={brandIsExternal ? "_blank" : undefined}
                >
                  <img
                    src={logoUrl}
                    alt={intl.formatMessage(messages["footer.logo.altText"])}
                    height="40"
                  />
                </a>
              </li>
            </ul>
          </div>
          <nav className="nav-colophon">
            <ol>
              {indigoFooterNavLinks.map((link) => (
                <li key={link.url}>
                  <a href={`${link.url.startsWith("http") ? link.url : config.LMS_BASE_URL + link.url}`}>{link.title}</a>
                </li>
              ))}
            </ol>
          </nav>
        </div>
        <span className="copyright-site">
          {intl.formatMessage(messages["footer.copyright.text"])}
        </span>
      </footer>
    </div>
  );
};
const MobileViewHeader = () => {
  const config = getConfig();
  const intl = useIntl();
  const messages = {
    "mobile.view.header.logo.altText": {
      id: "mobile.view.header.logo.altText",
      defaultMessage: "My Open edX",
      description: "alt text for the mobile view header logo",
    },
  };

  const BASE_URL = config.LMS_BASE_URL;

  return (
    <>
      <style>
        {`
          #root .logo-image.logo-white {
            display: none;
          }
          [data-paragon-theme-variant="dark"] #root .logo-image {
            display: none;
          }
          [data-paragon-theme-variant="dark"] #root .logo-white {
            display: block;
          }
        `}
      </style>
      <a href={`${BASE_URL}/dashboard`} title="Open edX" className="logo">
        <img className="logo-image" src={`${BASE_URL}/static/indigo/images/logo.png`} alt={intl.formatMessage(messages["mobile.view.header.logo.altText"])} />
        <img className="logo-image logo-white" src={`${BASE_URL}/static/indigo/images/logo-white.png`} alt={intl.formatMessage(messages["mobile.view.header.logo.altText"])} />
      </a>
    </>
  );
};
const ThemedLogo = () => {
  const BASE_URL = getConfig().LMS_BASE_URL;
  // A site with its own brand profile serves its logo from brand-openedx. The
  // header is a night band in both themes, so that is its light wordmark.
  const siteLogo = getConfig().INDIGO_LOGO_WHITE_URL;
  const logoUrl = siteLogo || `${BASE_URL}/static/indigo/images/logo.png`;
  const logoWhiteUrl = siteLogo || `${BASE_URL}/static/indigo/images/logo-white.png`;
  const brandName = getConfig().INDIGO_BRAND_NAME || getConfig().SITE_NAME || "Home";
  // A site that hides registration (SHOW_REGISTRATION_LINKS false) should not
  // offer Sign Up in the header either; the stock header shows it regardless.
  const hideSignUp = getConfig().SHOW_REGISTRATION_LINKS === false;

  return (
    <>
      <style>
        {`
          #root header .logo-image.logo-white {
            display: none;
          }
          [data-paragon-theme-variant="dark"] #root header .logo-image {
            display: none;
          }
          [data-paragon-theme-variant="dark"] #root header .logo-white {
            display: block;
          }
          ${hideSignUp ? '#root header a[href*="/register"] { display: none; }' : ''}
        `}
      </style>
      <a href={`${BASE_URL}/dashboard`} title={brandName} className="logo">
        <img className="logo-image" src={logoUrl} alt={brandName} />
        <img className="logo-image logo-white" src={logoWhiteUrl} alt={brandName} />
      </a>
    </>
  );
};
const ToggleThemeButton = () => {
  const intl = useIntl();
  const [isDarkThemeEnabled, setIsDarkThemeEnabled] = useState(false);

  const themeCookieNames = [
    'selected-paragon-theme-variant',
    'selected-theme-variant',
  ];
  const themeAttributeNames = [
    'data-paragon-theme-variant',
    'data-theme-variant',
  ];
  const primaryCookie = themeCookieNames[0];
  const themeCookieExpiry = 90; // days
  const isThemeToggleEnabled = getConfig().INDIGO_ENABLE_DARK_TOGGLE;

  const getCookie = (name) => {
    return document.cookie
      .split("; ")
      .find((row) => row.startsWith(name + "="))
      ?.split("=")[1];
  };

  const getThemeCookie = () => {
    for (const name of themeCookieNames) {
      const value = getCookie(name);
      if (value !== undefined) {
        return value;
      }
    }
    return undefined;
  };

  const setCookie = (name, value, { domain, path, expires }) => {
    document.cookie = `${name}=${value}; domain=${domain}; path=${path}; expires=${expires.toUTCString()}; SameSite=Lax`;
  };

  const setThemeCookies = (value, opts) => {
    for (const name of themeCookieNames) {
      setCookie(name, value, opts);
    }
  };
  // The page's real variant. Dark is the configured default, so a first-time
  // visitor has no cookie yet: going by the cookie alone, the switch showed
  // "light" on a dark page and the first click "switched" to dark (no change).
  const getCurrentTheme = () => (
    document.documentElement.getAttribute(themeAttributeNames[0]) || getThemeCookie()
  );

  const setThemeAttribute = (theme) => {
    for (const attr of themeAttributeNames) {
      document.documentElement.setAttribute(attr, theme);
    }
  };

  const serverURL = new URL(getConfig().LMS_BASE_URL);

  const getCookieExpiry = () => {
    const today = new Date();
    return new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate() + themeCookieExpiry
    );
  };

  const getCookieOptions = (serverURL) => ({
    domain: serverURL.hostname,
    path: '/',
    expires: getCookieExpiry(),
  });

  const onToggleTheme = () => {
    let theme = '';

    if (getCurrentTheme() === 'dark') {
      setThemeAttribute('light');
      setIsDarkThemeEnabled(false);
      theme = 'light';
    } else {
      setThemeAttribute('dark');
      setIsDarkThemeEnabled(true);
      theme = 'dark';
    }

    for (const name of themeCookieNames) {
        window.localStorage.setItem(name, theme);
    }
    setTimeout(() => {
      setThemeCookies(theme, getCookieOptions(serverURL));
      window.location.reload();
    }, 1);
  };

  // frontend-platform sets the variant attribute once the theme CSS loads,
  // which can be after this renders; follow it so the switch matches the page.
  useEffect(() => {
    const sync = () => setIsDarkThemeEnabled(getCurrentTheme() === 'dark');
    sync();
    const observer = new MutationObserver(sync);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: [themeAttributeNames[0]] });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const cookie = getThemeCookie();
    if (!cookie || cookie === 'undefined') {
      return;
    }
    if (cookie !== window.localStorage.getItem(primaryCookie)) {
      for (const name of themeCookieNames) {
          window.localStorage.setItem(name, cookie);
      }
      window.location.reload();
    }
    document.documentElement.setAttribute(themeAttributeNames[0], cookie);
  }, []);

  const handleKeyUp = (event) => {
    if (event.key === "Enter") {
      onToggleTheme();
    }
  };

  if (!isThemeToggleEnabled) {
    return <div />;
  }

  const messages = {
    "header.user.theme": {
      id: "header.user.theme",
      defaultMessage: "Toggle Theme",
      description: "Toggle between light and dark theme",
    },
  };

  return (
    <div className="theme-toggle-button mr-3">
      <div className="light-theme-icon">
        <Icon src={WbSunny} />
      </div>
      <div className="toggle-switch">
        <label htmlFor="theme-toggle-checkbox" className="switch">
          <input
            id="theme-toggle-checkbox"
            checked={isDarkThemeEnabled}
            onChange={onToggleTheme}
            onKeyUp={handleKeyUp}
            type="checkbox"
            title={intl.formatMessage(messages["header.user.theme"])}
          />
          <span className="slider round" />
          <span id="theme-label" className="sr-only">{`Switch to ${isDarkThemeEnabled ? "Light" : "Dark"
            } Mode`}</span>
        </label>
      </div>
      <div className="dark-theme-icon">
        <Icon src={Nightlight} />
      </div>
    </div>
  );
};
// Flight-deck hero for the learner dashboard (course_list slot, priority 1).
// Derives every number from the dashboard's own data source
// (/api/learner_home/init) — no invented stats. Styled by
// brand-openedx paragon/_dashboard.scss (.sqa-* classes).

const SqaStatRing = ({ pct }) => {
  const C = 2 * Math.PI * 24;
  const [drawn, setDrawn] = useState(false);
  useEffect(() => {
    const raf = window.requestAnimationFrame(() => setDrawn(true));
    return () => window.cancelAnimationFrame(raf);
  }, []);
  const clamped = Math.max(0, Math.min(1, pct));
  // Inline width/fill/stroke are a fallback so the ring degrades to a small
  // outline (not a giant black disc) if the brand stylesheet ever fails to
  // load; the .sqa-ring CSS rules override these presentation attributes.
  return (
    <svg className="sqa-ring" viewBox="0 0 56 56" width="56" height="56" aria-hidden="true">
      <circle className="sqa-ring-track" cx="28" cy="28" r="24" fill="none" stroke="rgba(16,26,51,0.08)" strokeWidth="4" />
      <circle
        className="sqa-ring-fill"
        cx="28"
        cy="28"
        r="24"
        fill="none"
        stroke="currentColor"
        strokeWidth="4"
        strokeLinecap="round"
        strokeDasharray={C}
        strokeDashoffset={drawn ? C * (1 - clamped) : C}
        transform="rotate(-90 28 28)"
      />
    </svg>
  );
};

const SqaDashboardHero = () => {
  const config = getConfig();
  const authUser = getAuthenticatedUser();
  const [courses, setCourses] = useState(null);

  useEffect(() => {
    let alive = true;
    getAuthenticatedHttpClient()
      .get(`${config.LMS_BASE_URL}/api/learner_home/init`)
      .then((res) => {
        if (alive && res.data && Array.isArray(res.data.courses)) {
          setCourses(res.data.courses);
        }
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const abs = (url) => (url && url.indexOf('http') !== 0 ? `${config.LMS_BASE_URL}${url}` : url);

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : (hour < 18 ? 'Good afternoon' : 'Good evening');
  const dateLabel = new Date()
    .toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })
    .toUpperCase();

  let stats = null;
  let resume = null;
  if (courses && courses.length) {
    const active = (c) => c.enrollment && c.enrollment.isEnrolled && !(c.courseRun && c.courseRun.isArchived);
    const total = courses.length;
    const inProgress = courses.filter((c) => active(c) && c.enrollment.hasStarted).length;
    const notStarted = courses.filter((c) => active(c) && !c.enrollment.hasStarted).length;
    const certificates = courses.filter((c) => c.certificate && c.certificate.isEarned).length;
    stats = { total, inProgress, notStarted, certificates };

    const byLastEnrolled = (a, b) => new Date(b.enrollment.lastEnrolled || 0) - new Date(a.enrollment.lastEnrolled || 0);
    const started = courses
      .filter((c) => active(c) && c.enrollment.hasStarted && c.courseRun && c.courseRun.resumeUrl)
      .sort(byLastEnrolled);
    if (started.length) {
      resume = { mode: 'resume', target: started[0] };
    } else {
      const fresh = courses
        .filter((c) => active(c) && !c.enrollment.hasStarted && c.courseRun && c.courseRun.homeUrl)
        .sort(byLastEnrolled);
      if (fresh.length) {
        resume = { mode: 'begin', target: fresh[0] };
      }
    }
  }

  const statCards = stats ? [
    { key: 'inProgress', hue: 'olive', label: 'In progress', value: stats.inProgress },
    { key: 'notStarted', hue: 'viola', label: 'Not started', value: stats.notStarted },
    { key: 'certificates', hue: 'jade', label: 'Certificates', value: stats.certificates },
  ] : [];

  // NOTE: this file is rendered by Tutor's Jinja templating before it lands in
  // env.config.jsx — inline JSX style objects (double braces) are forbidden
  // because "{" + "{" opens a Jinja print statement. Hoist style objects.
  const resumeArtStyle = resume && resume.target.course && resume.target.course.bannerImgSrc
    ? { backgroundImage: `url(${abs(resume.target.course.bannerImgSrc)})` }
    : null;
  const statDelayStyle = (i) => ({ animationDelay: `${0.12 + i * 0.09}s` });

  return (
    <section id="sqa-dash-hero" aria-label="Dashboard overview">
      <p className="sqa-eyebrow">{dateLabel}</p>
      <h2 className="sqa-hero-title">
        {greeting}{authUser && authUser.username ? `, ${authUser.username}` : ''}
      </h2>

      {resume && (
        <a
          className="sqa-resume"
          href={abs(resume.mode === 'resume' ? resume.target.courseRun.resumeUrl : resume.target.courseRun.homeUrl)}
        >
          {resumeArtStyle ? (
            <span
              className="sqa-resume-art"
              style={resumeArtStyle}
              aria-hidden="true"
            />
          ) : null}
          <span className="sqa-resume-meta">
            <span className="sqa-micro">
              {resume.mode === 'resume' ? 'CONTINUE' : 'START'}
              {resume.target.course && resume.target.course.courseNumber ? ` · ${resume.target.course.courseNumber}` : ''}
            </span>
            <span className="sqa-resume-name">
              {resume.target.course ? resume.target.course.courseName : ''}
            </span>
            <span className="sqa-resume-track" aria-hidden="true" />
          </span>
          <span className="sqa-btn">
            {resume.mode === 'resume' ? 'Resume' : 'Begin'}
            <svg className="sqa-btn-arrow" viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
              <path d="M2 8h10M8 3l5 5-5 5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
        </a>
      )}

      {stats && (
        <div className="sqa-stats">
          {statCards.map((s, i) => (
            <div
              key={s.key}
              className={`sqa-stat sqa-stat--${s.hue}`}
              style={statDelayStyle(i)}
            >
              <span className="sqa-stat-dial">
                <SqaStatRing pct={stats.total ? s.value / stats.total : 0} />
                <span className="sqa-stat-num">{s.value}</span>
              </span>
              <span className="sqa-stat-meta">
                <span className="sqa-stat-label">{s.label}</span>
                <span className="sqa-stat-sub">of {stats.total} enrolled</span>
              </span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};
// Membership instrument rail for the learner dashboard (widget_sidebar slot,
// replaces the stock "Looking for a challenge?" card). Pulls live data from
// the sqa plugin API (api_membership waffle switch must be ON):
//   GET /sqa/api/levels/            -> tier ladder
//   GET /sqa/api/user/<username>/   -> current membership (404 = Free)
// Styled by brand-openedx paragon/_dashboard.scss (.sqa-* classes).

const SqaMembershipInstrument = () => {
  const config = getConfig();
  const authUser = getAuthenticatedUser();
  const [levels, setLevels] = useState(null);
  const [membership, setMembership] = useState(null);
  const [memberLoaded, setMemberLoaded] = useState(false);
  // A site that sells no plans (INDIGO_SHOW_MEMBERSHIP false) shows no panel.
  const hidden = config.INDIGO_SHOW_MEMBERSHIP === false;

  useEffect(() => {
    if (hidden) { return undefined; }
    let alive = true;
    const http = getAuthenticatedHttpClient();
    http.get(`${config.LMS_BASE_URL}/sqa/api/levels/`)
      .then((res) => {
        if (alive && Array.isArray(res.data) && res.data.length) {
          setLevels(res.data);
        }
      })
      .catch(() => {});
    if (authUser && authUser.username) {
      http.get(`${config.LMS_BASE_URL}/sqa/api/user/${authUser.username}/`)
        .then((res) => {
          if (alive) {
            setMembership(res.data);
            setMemberLoaded(true);
          }
        })
        .catch(() => {
          // 404 = no active membership row, i.e. Free tier
          if (alive) { setMemberLoaded(true); }
        });
    } else {
      setMemberLoaded(true);
    }
    return () => { alive = false; };
  }, []);

  const fallbackLevels = [
    { slug: 'free', display_name: 'Free', rank: 0 },
    { slug: 'basic', display_name: 'Basic', rank: 1 },
    { slug: 'premium', display_name: 'Premium', rank: 2 },
    { slug: 'enterprise', display_name: 'Enterprise', rank: 3 },
  ];
  const tierColors = {
    free: '#8E9CC0',
    basic: '#9F8BFF',
    premium: '#A9BC3F',
    enterprise: '#45C48A',
  };

  // Altimeter tape: highest tier on top.
  const tiers = (levels || fallbackLevels).slice().sort((a, b) => b.rank - a.rank);
  const currentSlug = membership && membership.level_slug ? membership.level_slug : 'free';
  const currentTier = tiers.find((t) => t.slug === currentSlug) || tiers[tiers.length - 1];
  const isTop = memberLoaded && currentTier && tiers.length && currentTier.slug === tiers[0].slug;
  const payBase = '/sqa-payment';   // same host as this page
  const validThru = membership && membership.end_date
    ? new Date(membership.end_date)
      .toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
      .toUpperCase()
    : null;

  if (hidden) { return null; }

  return (
    <aside id="sqa-rail">
      <section id="sqa-membership" aria-label="Membership level">
        <p className="sqa-eyebrow sqa-eyebrow--night">YOUR PLAN</p>
        <h3 className="sqa-mem-level">
          {memberLoaded && currentTier ? currentTier.display_name : '—'}
        </h3>
        {validThru ? <p className="sqa-mem-valid">VALID THRU {validThru}</p> : null}
        <ol className="sqa-gauge">
          {tiers.map((t) => {
            const isCurrent = memberLoaded && currentTier && t.slug === currentTier.slug;
            const unlocked = memberLoaded && currentTier && t.rank <= currentTier.rank;
            // hoisted style object — inline double-brace JSX styles break
            // Tutor's Jinja rendering of this file (see SqaDashboardHero.jsx)
            const tierStyle = { '--sqa-tier-c': tierColors[t.slug] || '#8E9CC0' };
            return (
              <li
                key={t.slug}
                className={`sqa-tier${isCurrent ? ' sqa-tier--current' : ''}${unlocked ? ' sqa-tier--unlocked' : ''}`}
                style={tierStyle}
              >
                <span className="sqa-tier-dot" aria-hidden="true" />
                <span className="sqa-tier-name">{t.display_name}</span>
                {isCurrent ? <span className="sqa-tier-flag">CURRENT</span> : null}
              </li>
            );
          })}
        </ol>
        <a className="sqa-btn sqa-btn--night" href={isTop ? `${payBase}/manage` : `${payBase}/`}>
          {isTop ? 'Manage membership' : 'Upgrade plan'}
          <svg className="sqa-btn-arrow" viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
            <path d="M2 8h10M8 3l5 5-5 5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </a>
        {!isTop && membership ? (
          <a className="sqa-mem-manage" href={`${payBase}/manage`}>Manage current plan</a>
        ) : null}
      </section>

      <a className="sqa-explore" href={`${config.LMS_BASE_URL}/courses`}>
        <span className="sqa-explore-meta">
          <span className="sqa-eyebrow">MORE COURSES</span>
          <span className="sqa-explore-title">Browse the catalog</span>
        </span>
        <span className="sqa-explore-arrow" aria-hidden="true">
          <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true">
            <path d="M2 8h10M8 3l5 5-5 5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
      </a>
    </aside>
  );
};
// Designed empty state for the learner dashboard (no_courses_view slot,
// replaces the stock empty-course illustration). Styled by
// brand-openedx paragon/_dashboard.scss (.sqa-* classes).

const SqaDashboardEmptyState = () => {
  const config = getConfig();
  return (
    <section id="sqa-empty">
      <span className="sqa-empty-ghost" aria-hidden="true">SQA</span>
      <p className="sqa-eyebrow sqa-eyebrow--center">NO ENROLLMENTS LOGGED</p>
      <h3 className="sqa-empty-title">Chart your first course</h3>
      <p className="sqa-empty-copy">
        Your flight deck is standing by. Pick a course from the catalog
        and your missions will appear here.
      </p>
      <a className="sqa-btn" href={`${config.LMS_BASE_URL}/courses`}>
        Explore courses
        <svg className="sqa-btn-arrow" viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
          <path d="M2 8h10M8 3l5 5-5 5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </a>
    </section>
  );
};
// AI token card for the Profile MFE (additional_profile_fields slot).
// Fetches the user's active SQA proxy tokens from the plugin API and shows
// status + a link to the full tokens management page (/sqa/tokens/).
// No inline double-brace style objects — Jinja renders this file before it
// lands in env.config.jsx, so a doubled curly brace is read as a template tag.

const SqaTokenCard = () => {
  const config = getConfig();
  const [tokens, setTokens] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const hidden = config.INDIGO_SHOW_AI_TOKENS === false;

  useEffect(() => {
    if (hidden) { return undefined; }
    let alive = true;
    getAuthenticatedHttpClient()
      .get(`${config.LMS_BASE_URL}/sqa/api/proxy/tokens/`)
      .then((res) => {
        if (alive && Array.isArray(res.data)) {
          setTokens(res.data.filter((t) => t.status === 'active'));
        }
      })
      .catch(() => {})
      .finally(() => { if (alive) setLoaded(true); });
    return () => { alive = false; };
  }, []);

  const tokenPageUrl = `${config.LMS_BASE_URL}/sqa/tokens/`;
  const activeToken = tokens[0] || null;

  if (hidden || !loaded) return null;

  return (
    <div className="pgn__form-group mb-4">
      <p className="pgn__form-label font-weight-bold mb-2">AI Access Token</p>
      {activeToken ? (
        <div className="d-flex align-items-center flex-wrap">
          <span className="badge badge-success mr-2">Active</span>
          <code className="mr-3 text-monospace small">{activeToken.token_prefix}…</code>
          <a className="btn btn-outline-primary btn-sm" href={tokenPageUrl}>
            Manage token
          </a>
        </div>
      ) : (
        <div>
          <p className="small text-muted mb-2">
            Get an AI access token to use with the SQA course bot.
          </p>
          <a className="btn btn-primary btn-sm" href={tokenPageUrl}>
            Get your AI token
          </a>
        </div>
      )}
    </div>
  );
};
// Catalog MFE (Verawood) homepage: banner + course list + course card.
//
// Every value comes from the platform. Courses, counts and facets come from
// the LMS course search (/search/unstable/v0/course_list_search/), the same
// endpoint the stock catalog uses. Level and tags are indexed from each
// course's other_course_settings by sqa_django_app (catalog_index.py).
//
// No level is named here. Which levels exist, and their order, come from
// getConfig().SQA_CATALOG_LEVELS (LMS setting SQA_CATALOG_LEVELS via
// MFE_CONFIG). A level the config doesn't know still shows, after the known ones.
//
// What the search API cannot do, this does not pretend to: sorting is by start
// date or default only, and "open now" is shown per card, never as a filter
// (it is a date range, and the API filters exact values only).
//
// Styled by brand-openedx paragon/_catalog.scss (.sqa-cat-* classes).
// NOTE: this file is rendered by Jinja before webpack sees it. Never write two
// opening braces in a row anywhere in it (so no inline style objects).

const SQA_CAT_PAGE_SIZE = 12;
const SQA_CAT_TAG_LIMIT = 6;

const sqaCatLevels = () => {
  const levels = getConfig().SQA_CATALOG_LEVELS;
  return Array.isArray(levels) ? levels : [];
};

// Known levels in config order, then anything else the index returned.
const sqaCatOrderLevels = (present) => {
  const known = sqaCatLevels().filter((lv) => present.indexOf(lv) !== -1);
  const extra = present.filter((lv) => known.indexOf(lv) === -1).sort();
  return known.concat(extra);
};

const sqaCatAbs = (url) => {
  if (!url) { return ''; }
  return url.indexOf('http') === 0 ? url : `${getConfig().LMS_BASE_URL}${url}`;
};

// Uploaded course images are always contentstore assets. Anything else is the
// platform's gray placeholder, which gets a drawn plate instead.
const sqaCatHasImage = (url) => Boolean(url) && url.indexOf('asset-v1') !== -1;

const sqaCatIsOpen = (start) => {
  if (!start) { return false; }
  const when = new Date(start);
  return !Number.isNaN(when.getTime()) && when <= new Date();
};

const sqaCatStateText = (start, advertisedStart) => {
  if (sqaCatIsOpen(start)) { return 'Open now'; }
  if (advertisedStart) { return `Starts ${advertisedStart}`; }
  const when = new Date(start);
  if (!start || Number.isNaN(when.getTime())) { return ''; }
  return `Starts ${when.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
};

const sqaCatAboutUrl = (courseId) => {
  const base = (getConfig().PUBLIC_PATH || '/').replace(/\/?$/, '/');
  return `${base}courses/${courseId}/about`;
};

// ---- the drawn plate, used when a course has no uploaded image ----------
const SQA_CAT_OLIVE = '#C6D465';
const sqaCatMark = (tag) => {
  const c = SQA_CAT_OLIVE;
  const s = `stroke="${c}" fill="none" stroke-width="2.2"`;
  const t = (tag || '').toLowerCase();
  if (t === 'ai') {
    return `<circle cx="120" cy="70" r="7" fill="${c}"/><circle cx="196" cy="48" r="5.5" fill="${c}" fill-opacity=".7"/>`
      + `<circle cx="205" cy="116" r="6.5" fill="${c}" fill-opacity=".5"/><circle cx="138" cy="126" r="5" fill="${c}" fill-opacity=".8"/>`
      + `<path d="M120 70 L196 48 M120 70 L205 116 M120 70 L138 126 M196 48 L205 116" ${s} stroke-opacity=".55"/>`;
  }
  if (t === 'coding') {
    return `<path d="M118 52 L92 88 L118 124" ${s}/><path d="M206 52 L232 88 L206 124" ${s}/>`
      + `<path d="M150 128 L174 48" ${s} stroke-opacity=".6"/>`;
  }
  if (t === 'science') {
    return `<ellipse cx="162" cy="88" rx="62" ry="24" ${s} stroke-opacity=".7"/>`
      + `<ellipse cx="162" cy="88" rx="62" ry="24" ${s} stroke-opacity=".45" transform="rotate(58 162 88)"/>`
      + `<circle cx="162" cy="88" r="9" fill="${c}"/>`;
  }
  if (t === 'math') {
    let g = '';
    for (let x = 112; x <= 212; x += 25) { g += `<path d="M${x} 46 V130" stroke="${c}" stroke-opacity=".22"/>`; }
    for (let y = 46; y <= 130; y += 21) { g += `<path d="M112 ${y} H212" stroke="${c}" stroke-opacity=".22"/>`; }
    return `${g}<path d="M112 130 Q162 30 212 130" ${s}/>`;
  }
  if (t === 'robotics') {
    return `<rect x="128" y="54" width="68" height="68" rx="10" ${s}/>`
      + `<path d="M162 30 V54 M162 122 V146 M104 88 H128 M196 88 H220" ${s} stroke-opacity=".6"/>`
      + `<circle cx="162" cy="88" r="10" fill="${c}" fill-opacity=".85"/>`;
  }
  if (t === 'design') {
    return `<path d="M104 128 C 138 40, 186 136, 220 48" ${s}/>`
      + `<circle cx="104" cy="128" r="5" fill="${c}"/><circle cx="220" cy="48" r="5" fill="${c}"/>`
      + `<path d="M104 128 L138 40 M220 48 L186 136" stroke="${c}" stroke-opacity=".3"/>`;
  }
  return `<path d="M112 120 L162 56 L212 120" ${s}/><circle cx="162" cy="56" r="6" fill="${c}"/>`;
};

const sqaCatEscape = (text) => String(text || '').replace(/[&<>"]/g, (ch) => (
  ch === '&' ? '&amp;' : ch === '<' ? '&lt;' : ch === '>' ? '&gt;' : '&quot;'
));

const sqaCatPlate = (code, tag) => {
  let ticks = '';
  for (let x = 16; x < 320; x += 26) { ticks += `<path d="M${x} 168 v5" stroke="#97AAD7" stroke-opacity=".26"/>`; }
  const svg = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 180" width="320" height="180">'
    + '<rect width="320" height="180" fill="#0A1226"/>'
    + sqaCatMark(tag) + ticks
    + `<text x="16" y="32" fill="#FFFFFF" font-family="JetBrains Mono, monospace" font-size="16" font-weight="600" letter-spacing="2">${sqaCatEscape(code)}</text>`
    + (tag ? `<text x="304" y="32" text-anchor="end" fill="#D4E27A" font-family="Hanken Grotesk, sans-serif" font-size="15" font-weight="700">${sqaCatEscape(tag)}</text>` : '')
    + '</svg>';
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
};

// ---- level gauge: one segment per configured level --------------------------
const SqaCatGauge = ({ level }) => {
  const levels = sqaCatLevels();
  const rank = levels.indexOf(level) + 1;
  if (!rank) { return null; }
  return (
    <span className="sqa-cat-gauge" aria-hidden="true">
      {levels.map((lv, i) => (
        <i key={lv} className={i < rank ? 'on' : ''} />
      ))}
    </span>
  );
};

// ---- the card: used by the homepage list and the full catalog page ----------
const SqaCatalogCard = ({
  courseId, title, code, level, tags, start, advertisedStart, imageUrl,
}) => {
  const tagList = Array.isArray(tags) ? tags : [];
  const open = sqaCatIsOpen(start);
  const state = sqaCatStateText(start, advertisedStart);
  const src = sqaCatHasImage(imageUrl) ? sqaCatAbs(imageUrl) : sqaCatPlate(code, tagList[0]);
  const label = [title, level, state].filter(Boolean).join('. ');
  return (
    <a className="sqa-cat-card" href={sqaCatAboutUrl(courseId)} aria-label={label}>
      <span className="sqa-cat-thumb"><img src={src} alt="" loading="lazy" /></span>
      <span className="sqa-cat-body">
        {level ? (
          <span className="sqa-cat-level"><SqaCatGauge level={level} />{level}</span>
        ) : null}
        <span className="sqa-cat-title">{title}</span>
        {tagList.length ? (
          <span className="sqa-cat-tags">
            {tagList.map((t) => <span key={t} className="sqa-cat-tag">{t}</span>)}
          </span>
        ) : null}
        <span className="sqa-cat-meta">
          <span className="sqa-cat-code">{code}</span>
          {state ? <span className={open ? 'sqa-cat-state open' : 'sqa-cat-state'}>{state}</span> : null}
        </span>
      </span>
    </a>
  );
};

// ---- search -----------------------------------------------------------------
const sqaCatSearch = ({
  pageIndex = 0, pageSize = SQA_CAT_PAGE_SIZE, filters = {}, byStart = false, searchString = '',
}) => {
  const form = new FormData();
  form.append('page_size', String(pageSize));
  form.append('page_index', String(pageIndex));
  form.append('enable_course_sorting_by_start_date', String(byStart));
  if (searchString) { form.append('search_string', searchString); }
  Object.keys(filters).forEach((key) => {
    (filters[key] || []).forEach((value) => form.append(key, value));
  });
  return getAuthenticatedHttpClient()
    .post(`${getConfig().LMS_BASE_URL}/search/unstable/v0/course_list_search/`, form)
    .then((res) => res.data || {});
};

const sqaCatFromHit = (hit) => {
  const d = (hit && hit.data) || {};
  const content = d.content || {};
  return {
    courseId: d.id || hit.id,
    title: content.display_name || d.display_name || d.number || '',
    code: d.number || content.number || '',
    level: typeof d.level === 'string' ? d.level : '',
    tags: Array.isArray(d.tags) ? d.tags : [],
    start: d.start,
    advertisedStart: d.advertised_start,
    imageUrl: d.image_url,
  };
};

const sqaCatCoursesUrl = (query) => {
  const base = (getConfig().PUBLIC_PATH || '/').replace(/\/?$/, '/');
  const q = (query || '').trim();
  return q ? `${base}courses?search_query=${encodeURIComponent(q)}` : `${base}courses`;
};

// Shared search box. `tone` is "night" on the dark banner, "day" on a page.
const SqaCatSearchForm = ({ initial = '', tone = 'night' }) => {
  const [query, setQuery] = useState(initial);
  const onSubmit = (e) => {
    e.preventDefault();
    window.location.assign(sqaCatCoursesUrl(query));
  };
  return (
    <form className={tone === 'day' ? 'sqa-cat-find sqa-cat-find-day' : 'sqa-cat-find'} role="search" onSubmit={onSubmit}>
      <label className="sqa-cat-sr" htmlFor="sqa-cat-q">Search courses</label>
      <input
        id="sqa-cat-q"
        type="search"
        value={query}
        placeholder="Search by title, tag, or course code"
        onChange={(e) => setQuery(e.target.value)}
      />
      <button type="submit">Search</button>
    </form>
  );
};

// ---- homepage banner ----------------------------------------------------------
// The course count lives in the list panel below, not here, so it is shown once.
const SqaCatalogBanner = () => (
  <section className="sqa-cat sqa-cat-banner">
    <div className="sqa-cat-wrap">
      <h1 className="sqa-cat-headline">
        {getConfig().SQA_CATALOG_HEADLINE || 'Every course we teach, from first steps to final projects.'}
      </h1>
      <SqaCatSearchForm />
    </div>
  </section>
);

// ---- the /courses page --------------------------------------------------------
// The stock page wraps every slot in a fixed-width container, so a full-bleed
// banner cannot work here. This is the same list on a light page instead; the
// stock search field and data table are hidden (see plugin.py).
const SqaCatalogCoursesPage = () => {
  const initial = new URLSearchParams(window.location.search).get('search_query') || '';
  return (
    <div className="sqa-cat sqa-cat-page">
      <h1 className="sqa-cat-page-title">Explore courses</h1>
      {initial ? (
        <p className="sqa-cat-page-sub">
          {`Results for "${initial}". `}
          <a href={sqaCatCoursesUrl('')}>Clear search</a>
        </p>
      ) : null}
      <SqaCatSearchForm initial={initial} tone="day" />
      <SqaCatalogList searchString={initial} flat />
    </div>
  );
};

// ---- course list with facets --------------------------------------------------
const SqaCatalogList = ({ searchString = '', flat = false }) => {
  const [level, setLevel] = useState(null);
  const [tag, setTag] = useState(null);
  const [byStart, setByStart] = useState(false);
  const [allTags, setAllTags] = useState(false);
  const [pageIndex, setPageIndex] = useState(0);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [grand, setGrand] = useState(null);
  const [aggs, setAggs] = useState({});
  const [status, setStatus] = useState('loading');
  const [attempt, setAttempt] = useState(0);

  // A filter change and the return to page one happen in the same handler, so
  // React batches them into ONE fetch. (A separate "reset page" effect would
  // race: the fetch could run with the new filter but the old page number.)
  const refine = (apply) => { apply(); setPageIndex(0); };
  const onSort = (e) => {
    const v = e.target.value === 'start';
    refine(() => setByStart(v));
  };

  useEffect(() => {
    let alive = true;
    const filters = {};
    if (level) { filters.level = [level]; }
    if (tag) { filters.tags = [tag]; }
    if (pageIndex === 0) { setStatus('loading'); }
    sqaCatSearch({ pageIndex, filters, byStart, searchString })
      .then((data) => {
        if (!alive) { return; }
        const hits = (data.results || []).map(sqaCatFromHit);
        setRows((prev) => (pageIndex === 0 ? hits : prev.concat(hits)));
        setTotal(data.total || 0);
        setAggs(data.aggs || {});
        if (!level && !tag && !searchString) { setGrand(data.total || 0); }
        setStatus('ready');
      })
      .catch(() => { if (alive) { setStatus('error'); } });
    return () => { alive = false; };
  }, [level, tag, byStart, pageIndex, attempt, searchString]);

  const levelTerms = (aggs.level && aggs.level.terms) || {};
  const tagTerms = (aggs.tags && aggs.tags.terms) || {};
  const levelNames = sqaCatOrderLevels(Object.keys(levelTerms));
  const tagNames = Object.keys(tagTerms).sort((a, b) => (tagTerms[b] - tagTerms[a]) || a.localeCompare(b));
  const shownTags = allTags ? tagNames : tagNames.slice(0, SQA_CAT_TAG_LIMIT);

  if (status === 'error') {
    return (
      <div className="sqa-cat sqa-cat-list">
        <div className="sqa-cat-wrap sqa-cat-empty">
          <h2>Courses couldn't load</h2>
          <p>The course search didn't respond. Check your connection, then try again.</p>
          <button type="button" className="sqa-cat-quiet" onClick={() => setAttempt(attempt + 1)}>Try again</button>
        </div>
      </div>
    );
  }

  if (status === 'ready' && grand === 0) {
    return (
      <div className="sqa-cat sqa-cat-list">
        <div className="sqa-cat-wrap sqa-cat-empty">
          <h2>No courses published yet</h2>
          <p>When a course team publishes in Studio, it shows up here within a few minutes.</p>
        </div>
      </div>
    );
  }

  const chip = (label, count, pressed, onClick, extra) => (
    <button type="button" className="sqa-cat-chip" aria-pressed={pressed ? 'true' : 'false'} onClick={onClick}>
      {extra}{label}{typeof count === 'number' ? <span className="sqa-cat-count">{count}</span> : null}
    </button>
  );

  const wrapClass = flat ? '' : 'sqa-cat-wrap';
  return (
    <div className={flat ? 'sqa-cat sqa-cat-list sqa-cat-list-flat' : 'sqa-cat sqa-cat-list'}>
      <div className={wrapClass}>
        <div className={flat ? 'sqa-cat-panel sqa-cat-panel-flat' : 'sqa-cat-panel'}>
          <div className="sqa-cat-controls">
            <p className="sqa-cat-total">
              {total === 1 ? '1 course' : `${total} courses`}
              {grand !== null && total !== grand ? <span className="sqa-cat-of">{` of ${grand}`}</span> : null}
            </p>
            <label className="sqa-cat-sort" htmlFor="sqa-cat-sort">
              Sort by
              <select id="sqa-cat-sort" value={byStart ? 'start' : 'default'} onChange={onSort}>
                <option value="default">Recommended</option>
                <option value="start">Start date</option>
              </select>
            </label>
          </div>
          <div className="sqa-cat-facets">
            {levelNames.length ? (
              <div className="sqa-cat-facet" role="group" aria-label="Level">
                <span className="sqa-cat-flabel">Level</span>
                {levelNames.map((lv) => (
                  <React.Fragment key={lv}>
                    {chip(lv, levelTerms[lv], level === lv, () => refine(() => setLevel(level === lv ? null : lv)), <SqaCatGauge level={lv} />)}
                  </React.Fragment>
                ))}
              </div>
            ) : null}
            {tagNames.length ? (
              <div className="sqa-cat-facet" role="group" aria-label="Tags">
                <span className="sqa-cat-flabel">Tags</span>
                {shownTags.map((t) => (
                  <React.Fragment key={t}>
                    {chip(t, tagTerms[t], tag === t, () => refine(() => setTag(tag === t ? null : t)))}
                  </React.Fragment>
                ))}
                {tagNames.length > SQA_CAT_TAG_LIMIT ? (
                  <button type="button" className="sqa-cat-chip sqa-cat-more" onClick={() => setAllTags(!allTags)}>
                    {allTags ? 'Fewer tags' : `${tagNames.length - SQA_CAT_TAG_LIMIT} more tags`}
                  </button>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>

        {status === 'loading' ? (
          <div className="sqa-cat-grid" aria-busy="true">
            {[0, 1, 2, 3].map((i) => <div key={i} className="sqa-cat-card sqa-cat-skeleton" />)}
          </div>
        ) : null}

        {status === 'ready' && rows.length === 0 ? (
          <div className="sqa-cat-empty">
            {searchString && !level && !tag ? (
              <React.Fragment>
                <h2>{`No courses match "${searchString}"`}</h2>
                <p>Try a shorter word, a tag such as AI, or a course code.</p>
              </React.Fragment>
            ) : (
              <React.Fragment>
                <h2>Nothing matches those filters</h2>
                <p>Remove one to see more. The number on each filter shows what it would return.</p>
              </React.Fragment>
            )}
          </div>
        ) : null}

        {status === 'ready' && rows.length ? (
          <div className="sqa-cat-grid">
            {rows.map((r) => <SqaCatalogCard key={r.courseId} {...r} />)}
          </div>
        ) : null}

        {status === 'ready' && rows.length < total ? (
          <div className="sqa-cat-morebar">
            <button type="button" className="sqa-cat-quiet" onClick={() => setPageIndex(pageIndex + 1)}>
              {`Show ${Math.min(SQA_CAT_PAGE_SIZE, total - rows.length)} more of ${total - rows.length}`}
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
};
// Header main menu, the same in every MFE.
//
// Each MFE builds its own header menu: the learner dashboard has Courses and
// Discover New, the catalog and the payment MFE only get the stock header's
// default. So the links changed as you moved between pages. This replaces the
// default contents of the desktop and mobile main-menu slots everywhere with
// one list (wired in plugin.py, SQA_HEADER_NAV_MFES).
//
// Links go through the LMS (/dashboard, /courses), which redirects to the right
// MFE on each environment. The payment MFE shares this host, so its links are
// relative.
// NOTE: rendered by Jinja before webpack. Never write two opening braces in a row.

const sqaNavActive = (match) => {
  const path = window.location.pathname;
  if (match === '/sqa-payment') {
    return path.indexOf('/sqa-payment') === 0 && path.indexOf('/sqa-payment/pathways') !== 0;
  }
  return path.indexOf(match) === 0;
};

const SqaNavLink = ({ href, label, match }) => (
  <a className={`nav-link${sqaNavActive(match) ? ' active' : ''}`} href={href}>{label}</a>
);

const SqaMainMenu = () => {
  // Pathways only when its API answers: with the api_pathway switch off the
  // endpoint 404s, and a link to a dead page is worse than no link.
  const [pathways, setPathways] = useState(false);
  useEffect(() => {
    if (!getAuthenticatedUser()) { return undefined; }
    let alive = true;
    getAuthenticatedHttpClient().get(`${getConfig().LMS_BASE_URL}/sqa/api/pathways/`)
      .then(() => { if (alive) { setPathways(true); } })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const lms = getConfig().LMS_BASE_URL;
  return (
    <>
      <SqaNavLink href={`${lms}/dashboard`} label="Courses" match="/learner-dashboard" />
      <SqaNavLink href={`${lms}/courses`} label="Discover New" match="/catalog" />
      {getConfig().INDIGO_SHOW_MEMBERSHIP !== false
        && <SqaNavLink href="/sqa-payment" label="Membership" match="/sqa-payment" />}
      {pathways && <SqaNavLink href="/sqa-payment/pathways" label="Pathways" match="/sqa-payment/pathways" />}
    </>
  );
};
// Catalog course about page: the enroll button, plus the course's membership
// requirement.
//
// The stock button shows "An error occurred. Please try again later." for any
// failed enrollment, even when the LMS says why (sqa_django_app answers 400
// "User has no active membership"). This one shows the requirement before the
// click, and says plainly why a learner can't enroll, with a link to the plans.
//
// Eligibility comes from sqa_django_app (/sqa/api/course/<id>/check/<user>/), the
// same check the enrollment gate runs. The requirement and the learner's plan
// are only used to word the message. The APIs need a login, so anonymous
// visitors see the stock button, which sends them to sign in.
// NOTE: rendered by Jinja before webpack. Never write two opening braces in a row.

const SQA_ACCOUNT_TYPE_LABELS = {
  student: 'student',
  'teacher-pd': 'teacher PD',
  professional: 'professional',
  'scient-staff': 'Scient staff',
};

const sqaAboutCourseId = () => {
  const m = window.location.pathname.match(/\/courses\/([^/]+)\/about/);
  return m ? decodeURIComponent(m[1]) : null;
};

const sqaTagLabel = (tag) => SQA_ACCOUNT_TYPE_LABELS[tag] || tag.replace(/-/g, ' ');

// Why this learner can't enroll, in words. null when nothing in the requirement
// explains it (then the server's own reason is shown).
const sqaBlockedReason = (req, membership) => {
  if (!req) { return null; }
  const level = req.required_level ? req.required_level_display : null;
  if (!membership) {
    return level
      ? `This course needs the ${level} plan. You don't have a membership yet.`
      : 'This course needs a membership.';
  }
  if (level && req.required_rank != null && membership.rank < req.required_rank) {
    return `This course needs the ${level} plan. You're on ${membership.level_display}.`;
  }
  const have = (membership.tags || []).concat([membership.account_type]);
  const missing = (req.required_tags || []).filter((t) => have.indexOf(t) === -1);
  if (missing.length) {
    return `This course is only for ${missing.map(sqaTagLabel).join(', ')} accounts.`;
  }
  return null;
};

// A level can be bought; a missing tag or account type can't.
const sqaCanUpgrade = (req, membership) => {
  if (!req || !req.required_level) { return false; }
  return !membership || req.required_rank == null || membership.rank < req.required_rank;
};

const SqaCourseRequirement = ({ req }) => {
  if (!req) { return null; }
  const parts = [];
  if (req.required_level) { parts.push(`${req.required_level_display} plan`); }
  if ((req.required_tags || []).length) {
    parts.push(`${req.required_tags.map(sqaTagLabel).join(', ')} accounts`);
  }
  if (!parts.length) { return null; }
  return <p className="sqa-enroll-req mb-2">Requires: {parts.join(' · ')}</p>;
};

const SqaCourseAboutEnroll = ({ ecommerceCheckout, onEcommerceCheckout, onEnroll, isEnrollmentPending }) => {
  const user = getAuthenticatedUser();
  const courseId = sqaAboutCourseId();
  const [req, setReq] = useState(null);
  const [membership, setMembership] = useState(null);
  const [eligible, setEligible] = useState(null);
  const [error, setError] = useState(null);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    if (!user || !courseId) { return undefined; }
    let alive = true;
    const http = getAuthenticatedHttpClient();
    const base = `${getConfig().LMS_BASE_URL}/sqa/api`;
    const course = encodeURIComponent(courseId);
    const name = encodeURIComponent(user.username);
    http.get(`${base}/course/${course}/requirement/`)
      .then(({ data }) => { if (alive) { setReq(data); } })
      .catch(() => {});
    http.get(`${base}/user/${name}/`)
      .then(({ data }) => { if (alive) { setMembership(data); } })
      .catch(() => {});
    http.get(`${base}/course/${course}/check/${name}/`)
      .then(({ data }) => { if (alive) { setEligible(!!data.eligible); } })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const lms = getConfig().LMS_BASE_URL;
  const plans = <a className="btn btn-primary" href="/sqa-payment">See membership plans</a>;

  // Paid seats, and anonymous visitors: the stock flow already does the right thing.
  if (ecommerceCheckout || !user || !courseId) {
    return (
      <button type="button" className="btn btn-primary" disabled={isEnrollmentPending}
        onClick={ecommerceCheckout ? onEcommerceCheckout : onEnroll}>
        {isEnrollmentPending ? 'Enrolling...' : 'Enroll now'}
      </button>
    );
  }

  const enroll = () => {
    setPending(true);
    setError(null);
    const form = new URLSearchParams();
    form.append('course_id', courseId);
    form.append('enrollment_action', 'enroll');
    getAuthenticatedHttpClient().post(`${lms}/change_enrollment`, form.toString(), {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' },
    })
      .then(({ data }) => { window.location.href = data || `${lms}/dashboard`; })
      .catch((err) => {
        const attrs = (err && err.customAttributes) || {};
        const status = attrs.httpErrorStatus || (err && err.response && err.response.status);
        if (status === 403) {
          const next = `/courses/${courseId}/about`;
          window.location.href = `${getConfig().LOGIN_URL}?next=${encodeURIComponent(next)}`;
          return;
        }
        const body = attrs.httpErrorResponseData || (err && err.response && err.response.data);
        setError(sqaBlockedReason(req, membership)
          || (status === 400 && typeof body === 'string' && body)
          || 'Something went wrong. Please try again.');
        setPending(false);
      });
  };

  const blocked = eligible === false;
  const message = error || (blocked ? (sqaBlockedReason(req, membership) || 'You can\'t enroll in this course yet.') : null);

  return (
    <div className="sqa-enroll d-flex flex-column align-items-start">
      <SqaCourseRequirement req={req} />
      {message && <p role="status" className="sqa-enroll-msg text-danger mb-2">{message}</p>}
      {blocked
        ? (sqaCanUpgrade(req, membership) && plans)
        : (
          <button type="button" className="btn btn-primary" disabled={pending} onClick={enroll}>
            {pending ? 'Enrolling...' : 'Enroll now'}
          </button>
        )}
      {error && !blocked && sqaCanUpgrade(req, membership) && <div className="mt-2">{plans}</div>}
    </div>
  );
};
// Learner dashboard: send a new user to the plans page until they pick one.
//
// sqa_django_app gives every new user Free with plan_chosen=false. Once they
// have verified their email, the dashboard hands them to the payment MFE, which
// shows its welcome plans page (Basic recommended, or "Continue with Free").
// Paying or choosing Free sets plan_chosen, and this stops. Unverified users
// stay on the dashboard, where the stock banner asks them to confirm the email;
// nobody can pay before that (the checkout API refuses).
// Renders nothing.
// NOTE: rendered by Jinja before webpack. Never write two opening braces in a row.

const SqaPlanRedirect = () => {
  useEffect(() => {
    if (!getAuthenticatedUser() || getConfig().INDIGO_SHOW_MEMBERSHIP === false) { return; }
    getAuthenticatedHttpClient()
      .get(`${getConfig().LMS_BASE_URL}/sqa/api/billing/status/`)
      .then(({ data }) => {
        if (data && data.email_verified === true && data.plan_chosen === false) {
          window.location.replace('/sqa-payment/');
        }
      })
      .catch(() => {});
  }, []);
  return null;
};
// Learning MFE course-end page (course_exit_view_courses.v1 slot). Replaces the
// stock exit page: the stock body after this element is hidden by brand-openedx
// paragon/_course-exit.scss (.sqa-course-complete ~ *). If the data call fails
// this renders nothing and the stock page shows, so the learner is never stuck.
// Data: the same /api/courseware/course/<id> the stock page uses.
// NOTE: rendered by Jinja before webpack. Never write two opening braces in a row.

const SQA_CC_CERT_COPY = {
  downloadable: {
    title: 'Your certificate is ready',
    body: 'It stays on your dashboard and profile. Share it or download it any time.',
  },
  requesting: {
    title: 'Claim your certificate',
    body: 'You earned it. Generate it now and it will appear on your dashboard and profile.',
  },
  earned_but_not_available: {
    title: 'Your certificate is on its way',
    body: 'You earned it. It becomes available on',
  },
  generating: {
    title: 'Your certificate is being prepared',
    body: 'This usually takes a minute. Refresh the page to see it.',
  },
};

const SqaCourseComplete = () => {
  const config = getConfig();
  const match = window.location.pathname.match(/^(.*)\/course\/([^/]+)\/course-end/);
  const mfeBase = match ? match[1] : '';
  const courseId = match ? decodeURIComponent(match[2]) : null;
  const [meta, setMeta] = useState(null);
  const [failed, setFailed] = useState(false);
  const [claiming, setClaiming] = useState(false);

  const load = () => getAuthenticatedHttpClient()
    .get(`${config.LMS_BASE_URL}/api/courseware/course/${encodeURIComponent(courseId)}`)
    .then(({ data }) => setMeta(data))
    .catch(() => setFailed(true));

  useEffect(() => {
    if (courseId) { load(); } else { setFailed(true); }
  }, [courseId]);

  if (failed) { return null; }
  if (!meta) { return <section className="sqa-course-complete sqa-cc-loading" aria-busy="true" />; }

  const cert = meta.certificate_data || {};
  const status = cert.cert_status;
  const passed = !!meta.user_has_passing_grade;
  const finished = passed || ['downloadable', 'requesting', 'earned_but_not_available', 'generating'].indexOf(status) !== -1;
  const certCopy = SQA_CC_CERT_COPY[status];
  const certUrl = cert.cert_web_view_url
    ? `${config.LMS_BASE_URL}${cert.cert_web_view_url}`
    : (cert.download_url || null);
  const availableOn = cert.certificate_available_date
    ? new Date(cert.certificate_available_date).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
    : null;

  const claim = () => {
    setClaiming(true);
    getAuthenticatedHttpClient()
      .post(`${config.LMS_BASE_URL}/courses/${courseId}/generate_user_cert`)
      .then(load)
      .catch(() => {})
      .then(() => setClaiming(false));
  };

  let eyebrow = 'Course complete';
  let lede = 'You made it to the end. Every unit, every activity, done.';
  if (!finished) {
    eyebrow = 'End of course';
    lede = meta.has_scheduled_content
      ? 'You reached the end of what is open so far. More units are on the way.'
      : 'You reached the last unit. Your grade is not passing yet, so revisit the graded activities on your progress page.';
  }

  const courseHome = `${mfeBase}/course/${courseId}/home`;
  const progress = `${mfeBase}/course/${courseId}/progress`;
  const next = [
    finished
      ? { key: 'course', label: 'Back to the course', sub: 'Revisit any unit', href: courseHome }
      : { key: 'progress', label: 'See my progress', sub: 'Find what is left to pass', href: progress },
    { key: 'dashboard', label: 'My courses', sub: 'Everything you are enrolled in', href: `${config.LMS_BASE_URL}/dashboard` },
    { key: 'discover', label: 'Discover new courses', sub: 'Find your next course', href: `${config.LMS_BASE_URL}/courses` },
  ];

  const arrow = (
    <svg className="sqa-cc-arrow" viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
      <path d="M2 8h10M8 3l5 5-5 5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );

  return (
    <section className={`sqa-course-complete ${finished ? 'is-finished' : 'is-open'}`} aria-labelledby="sqa-cc-title">
      <div className="sqa-cc-plate">
        <div className="sqa-cc-copy">
          <p className="sqa-cc-eyebrow">{eyebrow}</p>
          <h1 id="sqa-cc-title" className="sqa-cc-title">{meta.name}</h1>
          <p className="sqa-cc-lede">{lede}</p>

          {certCopy && (
            <div className="sqa-cc-cert">
              <p className="sqa-cc-cert-title">{certCopy.title}</p>
              <p className="sqa-cc-cert-body">
                {certCopy.body}
                {status === 'earned_but_not_available' ? ` ${availableOn || 'the certificate date'}.` : ''}
              </p>
              <div className="sqa-cc-actions">
                {status === 'downloadable' && certUrl && (
                  <a className="sqa-cc-btn" href={certUrl}>View my certificate {arrow}</a>
                )}
                {status === 'downloadable' && meta.linkedin_add_to_profile_url && (
                  <a className="sqa-cc-btn sqa-cc-btn--ghost" href={meta.linkedin_add_to_profile_url} target="_blank" rel="noopener noreferrer">
                    Add to LinkedIn
                  </a>
                )}
                {status === 'requesting' && (
                  <button type="button" className="sqa-cc-btn" onClick={claim} disabled={claiming}>
                    {claiming ? 'Generating…' : 'Get my certificate'}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        <div className={`sqa-cc-seal ${finished ? '' : 'is-open'}`} aria-hidden="true">
          <span className="sqa-cc-seal-rosette" />
          <svg className="sqa-cc-seal-mark" viewBox="0 0 48 48" width="48" height="48">
            {finished
              ? <path d="M13 25l7 7 15-16" fill="none" stroke="currentColor" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round" />
              : <path d="M10 24h22M26 16l8 8-8 8" fill="none" stroke="currentColor" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round" />}
          </svg>
        </div>
      </div>

      <nav className="sqa-cc-next" aria-label="What next">
        <p className="sqa-cc-next-label">What next</p>
        <div className="sqa-cc-next-grid">
          {next.map((n) => (
            <a key={n.key} className={`sqa-cc-next-card sqa-cc-next-card--${n.key}`} href={n.href}>
              <span className="sqa-cc-next-text">
                <span className="sqa-cc-next-title">{n.label}</span>
                <span className="sqa-cc-next-sub">{n.sub}</span>
              </span>
              {arrow}
            </a>
          ))}
        </div>
      </nav>
    </section>
  );
};// Learning MFE header (learning_help_slot): a plain way out of a course, back to
// the dashboard. The logo goes there too, but learners don't read a logo as an
// exit. Styled in brand-openedx (.sqa-exit-course).
// NOTE: rendered by Jinja before webpack. Never write two opening braces in a row.

const SqaExitCourse = () => (
  <a className="sqa-exit-course" href={`${getConfig().LMS_BASE_URL}/dashboard`}>
    <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
      <path d="M10 3L5 8l5 5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
    <span>Exit course</span>
  </a>
);
  addPlugins(config, 'org.openedx.frontend.layout.footer.v1', [
    {
        op: PLUGIN_OPERATIONS.Insert,
        widget: {
            id: 'indigo_footer',
            type: DIRECT_PLUGIN,
            priority: 1,
            RenderWidget: IndigoFooter,
        },
    },
]);
  addPlugins(config, 'desktop_secondary_menu_slot', [
    {
        op: PLUGIN_OPERATIONS.Insert,
        widget: {
            id: 'theme_switch_button',
            type: DIRECT_PLUGIN,
            RenderWidget: ToggleThemeButton,
        },
    },
]);
  addPlugins(config, 'mobile_header_slot', [
    {
        op: PLUGIN_OPERATIONS.Hide,
        widgetId: 'default_contents',
    },
    {
        op: PLUGIN_OPERATIONS.Insert,
        widget: {
            id: 'theme_switch_button',
            type: DIRECT_PLUGIN,
            RenderWidget: MobileViewHeader,
        },
    },
]);
  addPlugins(config, 'logo_slot', [
    {
        op: PLUGIN_OPERATIONS.Hide,
        widgetId: 'default_contents',
    },
    {
        op: PLUGIN_OPERATIONS.Insert,
        widget: {
            id: 'custom_logo',
            type: DIRECT_PLUGIN,
            RenderWidget: ThemedLogo,
        }
    }
]);
if (config.pluginSlots && config.pluginSlots['logo_slot']) {
  config.pluginSlots['logo_slot'].keepDefault = false;

  const logoInsertPlugins =
    config.pluginSlots['logo_slot'].plugins.filter(plugin => plugin.op === 'insert' && plugin.widget?.id === 'custom_logo');

  if (logoInsertPlugins.length > 1) {
    let kept = false;
    config.pluginSlots['logo_slot'].plugins =
      config.pluginSlots['logo_slot'].plugins.filter(plugin => {
        if (plugin.op === 'insert' && plugin.widget?.id === 'custom_logo') {
          if (kept) return false;
          kept = true;
        }
        return true;
      });
  }
}

if (config.pluginSlots && config.pluginSlots['org.openedx.frontend.layout.footer.v1']) {
  const footerPluginsToInsert =
    config.pluginSlots['org.openedx.frontend.layout.footer.v1'].plugins.filter(plugin => plugin.op === 'insert');

  if (footerPluginsToInsert.length > 2) {
  config.pluginSlots['org.openedx.frontend.layout.footer.v1'].plugins =
    config.pluginSlots['org.openedx.frontend.layout.footer.v1'].plugins.filter(plugin => {
      if (plugin.op === 'insert') {
        const widgetId = plugin.widget?.id;
        return widgetId !== 'indigo_footer';
      }
      return true;
    });
  }
}

// SQA: SqaMainMenu (SqaHeaderNav.jsx) already has Membership and Pathways, so
// hide the sqa_payment plugin's own links where it is in use. FPF applies
// operations in order and a Hide only finds widgets inserted before it; the
// sqa_payment inserts come after ours, so the hides go here, at the very end.
[
  ['org.openedx.frontend.layout.header_desktop_main_menu.v1', ''],
  ['org.openedx.frontend.layout.header_mobile_main_menu.v1', '-mobile'],
].forEach(([slot, suffix]) => {
  const sqaSlot = config.pluginSlots && config.pluginSlots[slot];
  if (!sqaSlot || !sqaSlot.plugins.some((p) => p.widget && p.widget.id === `sqa_main_menu${suffix}`)) { return; }
  ['sqa-membership-link', 'sqa-pathways-link'].forEach((id) => {
    sqaSlot.plugins.push({ op: PLUGIN_OPERATIONS.Hide, widgetId: `${id}${suffix}` });
  });
});

  return config;
}

export default setConfig;